import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import {
    FaGraduationCap, FaBriefcase, FaArrowRight, FaUsers,
    FaRocket, FaCheckCircle, FaBuilding, FaMapMarkerAlt,
    FaShieldAlt, FaTachometerAlt, FaUser, FaSignOutAlt, FaSignInAlt, FaArrowUp,
    FaFileAlt, FaGlobe, FaLeaf, FaHandHoldingHeart, FaUtensils, FaCode,
    FaUserTag, FaTruck, FaChartBar, FaPalette, FaMicroscope, FaStethoscope,
    FaHardHat, FaLaw, FaHeadset, FaCamera, FaLaptopCode,
    FaSearchDollar, FaNetworkWired, FaServer, FaUserSecret, FaWarehouse,
    FaStore, FaConciergeBell, FaTools, FaPlug, FaWater, FaBroom, FaCar,
    FaPlane, FaAnchor, FaTshirt, FaCut, FaGem, FaPaintBrush, FaDraftingCompass,
    FaHammer, FaCogs, FaProjectDiagram, FaChalkboardTeacher, FaBalanceScale,
    FaBullhorn, FaMicrophone, FaTheaterMasks, FaFilm, FaMusic,
    FaChevronLeft, FaChevronRight, FaDatabase,
    FaBolt, FaShip, FaDumbbell, FaGamepad, FaChartLine, FaEdit, FaMapMarkedAlt,
    FaShoppingCart, FaCloud, FaCameraRetro, FaCalendarAlt
} from 'react-icons/fa';
import { BookOpen, TrendingUp, Shield, Mail, Globe } from 'lucide-react';
import Swal from 'sweetalert2';
import Loader from '../shared/Loader';
import { API_URL } from '../../config';
import './PublicPortal.css';
import { COURSE_CATEGORIES } from '../../constants';

const PublicPortal = () => {
    const { user, loading: authLoading, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const dropdownRef = useRef(null);

    const [courses, setCourses] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dropOpen, setDropOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Scroll listener for Scroll-to-Top button
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 400) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropOpen(false);
            }
            // Close mega menu when clicking outside
            if (!e.target.closest('.pp-nav-link-wrap')) {
                setActiveMegaMenu(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const [searchQuery, setSearchQuery] = useState('');
    const [categoryPage, setCategoryPage] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    const [placeholder, setPlaceholder] = useState('');
    const [allJobs, setAllJobs] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [courseCategoryPage, setCourseCategoryPage] = useState(0);

    const categoryIcons = {
        'Software Development': <FaCode />,
        'Healthcare & Medical': <FaStethoscope />,
        'Finance & Accounting': <FaSearchDollar />,
        'Engineering & Manufacturing': <FaCogs />,
        'Education & Training': <FaChalkboardTeacher />,
        'Sales & Marketing': <FaBullhorn />,
        'Human Resources': <FaUsers />,
        'Administration & Office Support': <FaShieldAlt />,
        'Customer Service': <FaHeadset />,
        'Hospitality & Tourism': <FaConciergeBell />,
        'Logistics & Supply Chain': <FaWarehouse />,
        'Design & Creative': <FaPalette />,
        'Media & Communications': <FaCamera />,
        'Legal': <FaBalanceScale />,
        'Science & Research': <FaMicroscope />,
        'Construction & Trades': <FaHardHat />,
        'Retail & Consumer Goods': <FaStore />,
        'Data Science & Analytics': <FaChartBar />,
        'Banking & Insurance': <FaBriefcase />,
        'Telecommunications': <FaPlug />,
        'Government & Public Sector': <FaBuilding />,
        'NGO & Non-Profit': <FaGlobe />,
        'Real Estate': <FaBuilding />,
        'Automotive': <FaCar />,
        'IT & Software': <FaCode />,
        'Agriculture & Farming': <FaLeaf />,
        'Energy & Utilities': <FaBolt />,
        'Fashion & Apparel': <FaTshirt />,
        'Food & Beverage': <FaUtensils />,
        'Security & Law Enforcement': <FaShieldAlt />,
        'Aviation & Aerospace': <FaPlane />,
        'Marine & Shipping': <FaShip />,
        'Sports & Fitness': <FaDumbbell />,
        'Gaming & Animation': <FaGamepad />,
        'Digital Marketing': <FaChartLine />,
        'Content Writing': <FaEdit />,
        'Travel & Leisure': <FaMapMarkedAlt />,
        'E-commerce': <FaShoppingCart />,
        'Cybersecurity': <FaUserSecret />,
        'Cloud Computing': <FaCloud />,
        'Quality Assurance': <FaCheckCircle />,
        'Project Management': <FaProjectDiagram />,
        'Art & Photography': <FaCameraRetro />,
        'Music & Entertainment': <FaMusic />,
        'Interior Design': <FaPaintBrush />,
        'Social Services': <FaHandHoldingHeart />,
        'Mining & Extraction': <FaHardHat />,
        'Event Management': <FaCalendarAlt />,
        'Others': <FaTools />
    };

    const courseCategories = COURSE_CATEGORIES.map(name => ({
        name,
        icon: categoryIcons[name] || <FaTools />
    }));

    const [activeMegaMenu, setActiveMegaMenu] = useState(null); // 'courses' | 'jobs' | null

    const navCategories = {
        courses: {
            featured: {
                title: "Popular Certifications",
                desc: "Get certified by top industry leaders and boost your career prospects.",
                action: "Explore All Courses"
            },
            items: [
                { name: 'Software Development', icon: <FaLaptopCode />, color: '#6366f1', desc: 'Build modern applications' },
                { name: 'AI & Machine Learning', icon: <FaMicroscope />, color: '#8b5cf6', desc: 'Master future technology' },
                { name: 'UI/UX Design', icon: <FaPalette />, color: '#c9971c', desc: 'Create stunning interfaces' },
                { name: 'Digital Marketing', icon: <FaBullhorn />, color: '#f59e0b', desc: 'Grow brands online' },
                { name: 'Cloud Computing', icon: <FaServer />, color: '#0ea5e9', desc: 'Manage scalable infra' },
                { name: 'Data Science', icon: <FaChartBar />, color: '#10b981', desc: 'Analyze complex data' }
            ]
        },
        jobs: {
            featured: {
                title: "Top Hiring Sectors",
                desc: "Explore thousands of openings from verified companies in trending industries.",
                action: "View All Jobs"
            },
            items: [
                { name: 'Finance & Accounting', icon: <FaSearchDollar />, color: '#c9971c', desc: 'Banking & fintech roles' },
                { name: 'IT & Software', icon: <FaLaptopCode />, color: '#0ea5e9', desc: 'Tech & network jobs' },
                { name: 'Sales & Marketing', icon: <FaBullhorn />, color: '#f43f5e', desc: 'Revenue & growth roles' },
                { name: 'Engineering & Manufacturing', icon: <FaDraftingCompass />, color: '#8b5cf6', desc: 'Build the physical world' },
                { name: 'Human Resources', icon: <FaUsers />, color: '#10b981', desc: 'People & talent mgmt' },
                { name: 'Healthcare & Medical', icon: <FaStethoscope />, color: '#ef4444', desc: 'Healthcare & research' }
            ]
        }
    };

    // Typewriter effect for placeholder
    useEffect(() => {
        const phrases = [
            'Search for "Software Engineer"',
            'Search for "Project Manager"',
            'Search for "Marketing Executive"',
            'Search for "Data Scientist"',
            'Search for "HR Manager"',
            'Search for "UI/UX Designer"',
            'Search for "Business Analyst"',
            'Search for "Customer Support"',
            'Search for "Full Stack Developer"',
            'Search for "Operations Manager"',
            'Search for "Digital Marketing Course"',
            'Search for "Cloud Computing"',
            'Search for "Accountant"',
            'Search for "Sales Representative"',
            'Search for "Cybersecurity Associate"',
            'Search for "Content Strategist"',
            'Search for "Civil Engineer"',
            'Search for "Mobile App Developer"',
            'Search for "Legal Consultant"',
            'Search for "Hospitality Manager"'
        ];
        let phraseIdx = 0;
        let charIdx = 0;
        let isDeleting = false;
        let timeout;

        const type = () => {
            const currentPhrase = phrases[phraseIdx];
            if (isDeleting) {
                setPlaceholder(currentPhrase.substring(0, charIdx - 1));
                charIdx--;
            } else {
                setPlaceholder(currentPhrase.substring(0, charIdx + 1));
                charIdx++;
            }

            let typeSpeed = isDeleting ? 50 : 100;

            if (!isDeleting && charIdx === currentPhrase.length) {
                isDeleting = true;
                typeSpeed = 2000; // Pause at end
            } else if (isDeleting && charIdx === 0) {
                isDeleting = false;
                phraseIdx = (phraseIdx + 1) % phrases.length;
                typeSpeed = 500;
            }

            timeout = setTimeout(type, typeSpeed);
        };

        type();
        return () => clearTimeout(timeout);
    }, []);

    // Fetch all data for searching
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [cRes, jRes] = await Promise.all([
                    axios.get('/api/public/courses'),
                    axios.get('/api/public/jobs')
                ]);
                const coursesData = cRes.data || [];
                const jobsData = jRes.data || [];
                setAllCourses(coursesData);
                setAllJobs(jobsData);
                setCourses(coursesData.slice(0, 4));
                setJobs(jobsData.slice(0, 4));
            } catch (err) {
                console.error("Error fetching public data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Handle search suggestions
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSuggestions([]);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filteredJobs = allJobs.filter(j =>
            j.title.toLowerCase().includes(query) ||
            j.company.toLowerCase().includes(query) ||
            j.category?.toLowerCase().includes(query)
        ).map(j => ({ ...j, type: 'job' }));

        const filteredCourses = allCourses.filter(c =>
            c.title.toLowerCase().includes(query) ||
            c.category?.toLowerCase().includes(query)
        ).map(c => ({ ...c, type: 'course' }));

        setSuggestions([...filteredJobs, ...filteredCourses].slice(0, 8));
    }, [searchQuery, allJobs, allCourses]);

    const handleSuggestionClick = (item) => {
        if (item.type === 'job') {
            navigate(`/browse/jobs?q=${encodeURIComponent(item.title)}`);
        } else {
            navigate(`/browse/courses?q=${encodeURIComponent(item.title)}`);
        }
        setSearchQuery('');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        const hasJobs = suggestions.some(s => s.type === 'job');
        const hasCourses = suggestions.some(s => s.type === 'course');

        if (hasCourses && !hasJobs) {
            navigate(`/browse/courses?q=${encodeURIComponent(searchQuery)}`);
        } else {
            navigate(`/browse/jobs?q=${encodeURIComponent(searchQuery)}`);
        }
    };

    const categories = [
        { label: 'Software Developer', icon: <FaLaptopCode />, path: '/browse/jobs?q=Software%20Developer', count: allJobs.filter(j => j.title.toLowerCase().includes('software')).length },
        { label: 'Internship', icon: <FaBriefcase />, path: '/browse/jobs?type=Internship', count: allJobs.filter(j => j.type === 'Internship').length },
        { label: 'Part-time', icon: <FaBriefcase />, path: '/browse/jobs?type=Part-time', count: allJobs.filter(j => j.type === 'Part-time').length },
        { label: 'Project Manager', icon: <FaProjectDiagram />, path: '/browse/jobs?q=Project%20Manager', count: allJobs.filter(j => j.title.toLowerCase().includes('project manager')).length },
        { label: 'Remote', icon: <FaGlobe />, path: '/browse/jobs?type=Remote', count: allJobs.filter(j => (j.type === 'Remote' || j.location?.toLowerCase().includes('remote'))).length },
        { label: 'Data Scientist', icon: <FaChartBar />, path: '/browse/jobs?category=Data%20Science%20%26%20Analytics', count: allJobs.filter(j => j.category === 'Data Science & Analytics').length },
        { label: 'IT & Software', icon: <FaLaptopCode />, path: '/browse/jobs?category=IT%20%26%20Software', count: allJobs.filter(j => j.category === 'IT & Software').length + allCourses.filter(c => c.category?.includes('IT')).length },
        { label: 'Marketing', icon: <FaBullhorn />, path: '/browse/jobs?category=Sales%20%26%20Marketing', count: allJobs.filter(j => j.category === 'Sales & Marketing').length },
        { label: 'HR Admin', icon: <FaUsers />, path: '/browse/jobs?category=Human%20Resources', count: allJobs.filter(j => j.category === 'Human Resources').length },
        { label: 'Finance', icon: <FaSearchDollar />, path: '/browse/jobs?category=Finance%20%26%20Accounting', count: allJobs.filter(j => j.category === 'Finance & Accounting').length },
    ];

    const getDashboardPath = (role) => {
        switch (role) {
            case 'student': return '/student-dashboard';
            case 'job_seeker': return '/jobseeker-dashboard';
            case 'recruiter': return '/recruiter';
            default: return '/admin';
        }
    };
    const getProfilePath = (role) => {
        switch (role) {
            case 'student': return '/student-dashboard/profile';
            case 'job_seeker': return '/jobseeker-dashboard/profile';
            case 'recruiter': return '/recruiter/profile';
            default: return '/admin/profile';
        }
    };

    const handleDashboard = () => {
        if (!user) { navigate('/auth'); return; }
        navigate(getDashboardPath(user.role));
        setDropOpen(false);
    };
    const handleProfile = () => {
        if (!user) { navigate('/auth'); return; }
        navigate(getProfilePath(user.role));
        setDropOpen(false);
    };
    const handleLogout = () => {
        if (logout) logout();
        navigate('/');
        setDropOpen(false);
        setMobileNavOpen(false);
    };

    const handleCourseClick = (courseId) => {
        navigate(`/public-course/${courseId}`);
    };

    const scrollToSection = (e, id) => {
        e.preventDefault();
        setMobileNavOpen(false); // Close mobile nav if open
        const el = document.getElementById(id);
        if (el) {
            // Using smooth scroll into view
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleApply = (jobId) => {
        if (!user) {
            navigate('/auth');
            return;
        }
        // Allow both job_seeker and student to apply
        if (user.role === 'job_seeker' || user.role === 'student') {
            const path = user.role === 'job_seeker' ? '/jobseeker-dashboard' : '/student-dashboard';
            navigate(path);
            return;
        }
        // Any other role — tell them to create the right account
        Swal.fire({
            icon: 'warning',
            title: 'Candidate Account Required',
            html: `You're currently signed in as <strong>${user.role.replace('_', ' ')}</strong>.<br/>To apply for jobs, you need to be a <strong>Job Seeker</strong> or a <strong>Student</strong>.<br/><br/>Please register as a candidate to continue.`,
            confirmButtonText: 'Create Account',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1a3a6b'
        }).then(r => { if (r.isConfirmed) navigate('/auth?type=job_seeker'); });
    };

    const handleEnroll = (courseId) => {
        if (!user) {
            navigate('/auth');
            return;
        }
        if (user.role === 'student') {
            navigate(`/public-course/${courseId}`);
            return;
        }
        // Any other role — tell them to create the right account
        Swal.fire({
            icon: 'warning',
            title: 'Student Account Required',
            html: `You're currently signed in as <strong>${user.role.replace('_', ' ')}</strong>.<br/>Only <strong>Students</strong> can enroll in courses.<br/><br/>Please create a Student account to continue.`,
            confirmButtonText: 'Create Student Account',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1a3a6b'
        }).then(r => { if (r.isConfirmed) navigate('/auth?type=student'); });
    };

    const handleCreateAccount = () => {
        if (user) {
            // Already logged in — send to their dashboard
            navigate(getDashboardPath(user.role));
        } else {
            navigate('/auth');
        }
    };

    const getInitials = () => {
        if (!user?.name) return user?.email?.charAt(0).toUpperCase() || 'U';
        return user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    if (loading || authLoading) return <Loader fullScreen={true} />;

    return (
        <div className="pp-root" onClick={() => { setDropOpen(false); setSuggestions([]); }}>

            {/* ===== ADVANCED NAVBAR ===== */}
            <nav className="pp-nav">
                <div className="pp-nav-inner">
                    <div className="pp-brand">
                        <img src="/logo.png" alt="ZenXStaffing" className="pp-logo" />
                    </div>
                    <div className="pp-nav-links">
                        <div
                            className={`pp-nav-link-wrap ${activeMegaMenu === 'courses' ? 'active' : ''}`}
                            onMouseEnter={() => setActiveMegaMenu('courses')}
                            onMouseLeave={() => setActiveMegaMenu(null)}
                            onClick={(e) => {
                                if (window.innerWidth <= 1024) {
                                    e.preventDefault();
                                    setActiveMegaMenu(activeMegaMenu === 'courses' ? null : 'courses');
                                }
                            }}
                        >
                            <a href="#courses" onClick={(e) => {
                                if (window.innerWidth > 1024) {
                                    scrollToSection(e, 'courses');
                                } else {
                                    e.preventDefault();
                                }
                            }}>Courses</a>
                            {activeMegaMenu === 'courses' && (
                                <div className="pp-mega-menu">
                                    <div className="pp-mega-container">
                                        <div className="pp-mega-sidebar">
                                            <div className="pp-mega-featured-icon"><FaGraduationCap /></div>
                                            <h3>{navCategories.courses.featured.title}</h3>
                                            <p>{navCategories.courses.featured.desc}</p>
                                            <button className="pp-mega-prime-btn" onClick={() => navigate('/browse/courses')}>
                                                {navCategories.courses.featured.action} <FaArrowRight />
                                            </button>
                                        </div>
                                        <div className="pp-mega-main">
                                            <div className="pp-mega-grid">
                                                {navCategories.courses.items.map((cat, i) => (
                                                    <div
                                                        key={i}
                                                        className="pp-mega-item"
                                                        onClick={() => navigate(`/browse/courses?category=${encodeURIComponent(cat.name)}`)}
                                                    >
                                                        <div className="pp-mega-icon" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                                                            {cat.icon}
                                                        </div>
                                                        <div className="pp-mega-text">
                                                            <span>{cat.name}</span>
                                                            <p>{cat.desc}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pp-mega-pointer"></div>
                                </div>
                            )}
                        </div>

                        <div
                            className={`pp-nav-link-wrap ${activeMegaMenu === 'jobs' ? 'active' : ''}`}
                            onMouseEnter={() => setActiveMegaMenu('jobs')}
                            onMouseLeave={() => setActiveMegaMenu(null)}
                            onClick={(e) => {
                                if (window.innerWidth <= 1024) {
                                    e.preventDefault();
                                    setActiveMegaMenu(activeMegaMenu === 'jobs' ? null : 'jobs');
                                }
                            }}
                        >
                            <a href="#jobs" onClick={(e) => {
                                if (window.innerWidth > 1024) {
                                    scrollToSection(e, 'jobs');
                                } else {
                                    e.preventDefault();
                                }
                            }}>Jobs</a>
                            {activeMegaMenu === 'jobs' && (
                                <div className="pp-mega-menu">
                                    <div className="pp-mega-container">
                                        <div className="pp-mega-sidebar">
                                            <div className="pp-mega-featured-icon"><FaBriefcase /></div>
                                            <h3>{navCategories.jobs.featured.title}</h3>
                                            <p>{navCategories.jobs.featured.desc}</p>
                                            <button className="pp-mega-prime-btn" onClick={() => navigate('/browse/jobs')}>
                                                {navCategories.jobs.featured.action} <FaArrowRight />
                                            </button>
                                        </div>
                                        <div className="pp-mega-main">
                                            <div className="pp-mega-grid">
                                                {navCategories.jobs.items.map((cat, i) => (
                                                    <div
                                                        key={i}
                                                        className="pp-mega-item"
                                                        onClick={() => navigate(`/browse/jobs?category=${encodeURIComponent(cat.name)}`)}
                                                    >
                                                        <div className="pp-mega-icon" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                                                            {cat.icon}
                                                        </div>
                                                        <div className="pp-mega-text">
                                                            <span>{cat.name}</span>
                                                            <p>{cat.desc}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pp-mega-pointer"></div>
                                </div>
                            )}
                        </div>
                        <a href="#about" onClick={(e) => scrollToSection(e, 'about')}>About Us</a>
                        <a href="#resources" onClick={(e) => scrollToSection(e, 'resources')}>Resources</a>
                        <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>Contact</a>
                    </div>

                    <div className="pp-nav-cta">
                        {user ? (
                            <>
                                <button className="pp-nav-btn pp-nav-btn-gold" onClick={handleDashboard}>
                                    <FaTachometerAlt /> My Dashboard
                                </button>
                                <div className="pp-profile-wrap" onClick={(e) => e.stopPropagation()}>
                                    <div className="pp-avatar" onClick={() => setDropOpen(p => !p)}>
                                        {user.profileImage
                                            ? <img src={user.profileImage.startsWith('http') ? user.profileImage : `/${user.profileImage}`} alt="Profile" />
                                            : getInitials()
                                        }
                                    </div>
                                    {dropOpen && (
                                        <div className="pp-dropdown">
                                            <div className="pp-drop-header">
                                                <h4>{user.name || 'Welcome Back'}</h4>
                                                <p>{user.email}</p>
                                            </div>
                                            <div className="pp-drop-divider" />
                                            <button className="pp-drop-item" onClick={handleDashboard}>
                                                <FaTachometerAlt /> Dashboard
                                            </button>
                                            <button className="pp-drop-item" onClick={handleProfile}>
                                                <FaUser /> My Profile
                                            </button>
                                            <div className="pp-drop-divider" />
                                            <button className="pp-drop-item danger" onClick={handleLogout}>
                                                <FaSignOutAlt /> Sign Out
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <button className="pp-nav-btn pp-nav-btn-outline" onClick={() => navigate('/auth')}>
                                    <FaSignInAlt /> Sign In
                                </button>
                                <button className="pp-nav-btn pp-nav-btn-solid" onClick={() => navigate('/auth')}>
                                    Get Started
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {/* ===== REDESIGNED PREMIUM HERO ===== */}
            <section id="hero" className="pp-hero-premium">
                {/* Decorative Background Elements - Wrapped for clipping */}
                <div className="pp-hero-mesh-wrap">
                    <div className="pp-hero-mesh"></div>
                    <div className="pp-floating-sphere sphere-1"></div>
                    <div className="pp-floating-sphere sphere-2"></div>
                </div>
                <div className="pp-hero-glow-premium"></div>

                <div className="pp-hero-inner-premium">
                    <div className="pp-hero-content-wrap">
                        <div className="pp-hero-tagline">
                            <span className="pp-tag-line"></span>
                            <span className="pp-tag-text">Next-Generation Talent Acquisition</span>
                            <span className="pp-tag-line"></span>
                        </div>

                        <h1 className="pp-hero-title-premium">
                            Your Career Journey <br />
                            <span className="pp-text-highlight">Starts With ZenXStaffing</span>
                        </h1>
                        <p className="pp-hero-subtitle-premium">
                            Find your dream job amongst 2000+ vacancies or upskill with premium industry-vetted courses.
                        </p>

                        {/* Search Bar Container */}
                        <div className="pp-search-orchestrator" onClick={(e) => e.stopPropagation()}>
                            <div className="pp-search-glass-layer">
                                <form className="pp-search-form" onSubmit={handleSearchSubmit}>
                                    <div className="pp-search-main-input">
                                        <div className="pp-search-input-icon">
                                            <FaBriefcase />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder={placeholder}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onFocus={() => { if (searchQuery) setSuggestions([...suggestions]); }}
                                        />
                                    </div>
                                    <button type="submit" className="pp-search-action-btn">
                                        <FaSearchDollar /> <span>Search Now</span>
                                    </button>
                                </form>
                            </div>

                            {/* Suggestions Dropdown */}
                            {suggestions.length > 0 && (
                                <div className="pp-suggestions-curated">
                                    <div className="pp-sug-header">Top Results for "{searchQuery}"</div>
                                    <div className="pp-sug-list">
                                        {suggestions.map((item) => (
                                            <div
                                                key={item._id}
                                                className="pp-suggestion-card"
                                                onClick={() => handleSuggestionClick(item)}
                                            >
                                                <div className="pp-sug-card-icon">
                                                    {item.type === 'job' ? <FaBriefcase /> : <FaGraduationCap />}
                                                </div>
                                                <div className="pp-sug-card-info">
                                                    <h5>{item.title}</h5>
                                                    <p>{item.type === 'job' ? item.company : item.category}</p>
                                                </div>
                                                <div className={`pp-sug-card-badge ${item.type}`}>{item.type}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="pp-sug-full-view" onClick={() => {
                                        const hasJobs = suggestions.some(s => s.type === 'job');
                                        const hasCourses = suggestions.some(s => s.type === 'course');
                                        if (hasCourses && !hasJobs) {
                                            navigate(`/browse/courses?q=${encodeURIComponent(searchQuery)}`);
                                        } else {
                                            navigate(`/browse/jobs?q=${encodeURIComponent(searchQuery)}`);
                                        }
                                    }}>
                                        Explore all results <FaArrowRight />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Quick Action Categories */}
                        <div className="pp-hero-quick-cats">
                            <div className="pp-quick-cat-label">Popular Searches:</div>
                            <div className="pp-quick-cat-inner">
                                {categories.slice(0, 8).map((cat, i) => (
                                    <div
                                        key={i}
                                        className="pp-quick-cat-item"
                                        onClick={() => navigate(cat.path)}
                                    >
                                        <span className="pp-qc-icon">{cat.icon}</span>
                                        <span className="pp-qc-text">{cat.label}</span>
                                    </div>
                                ))}
                                <div className="pp-quick-cat-item more" onClick={() => navigate('/browse/jobs')}>
                                    <span className="pp-qc-text">Browse All</span>
                                    <FaArrowRight />
                                </div>
                            </div>
                        </div>

                        {/* Meta Stats Minimal */}
                        <div className="pp-hero-meta-grid">
                            {[
                                { count: "500+", label: "Courses", icon: <FaGraduationCap /> },
                                { count: "420+", label: "Hiring Now", icon: <FaBriefcase /> },
                                { count: "2.2k", label: "Vacancies", icon: <FaUsers /> },
                                { count: "400+", label: "Partners", icon: <FaBuilding /> }
                            ].map((stat, i) => (
                                <div key={i} className="pp-meta-stat-pill">
                                    <span className="pp-ms-icon">{stat.icon}</span>
                                    <span className="pp-ms-count">{stat.count}</span>
                                    <span className="pp-ms-label">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>


            {/* ===== FEATURES ===== */}
            <div className="pp-features">
                <div className="pp-features-grid">
                    {[
                        { icon: <FaGraduationCap />, title: "Industry Courses", desc: "Expert-curated programs in tech, design & business.", path: '/browse/courses' },
                        { icon: <FaBriefcase />, title: "Premium Jobs", desc: "Vetted listings from top-tier hiring companies.", path: '/browse/jobs' },
                        { icon: <FaUsers />, title: "Expert Mentors", desc: "Learn directly from industry professionals.", path: user ? (user.role === 'tutor' ? '/tutor-dashboard' : (user.role === 'student' ? '/student-dashboard' : '/jobseeker-dashboard')) : '/auth' },
                        { icon: <FaShieldAlt />, title: "Career Support", desc: "End-to-end placement support until you get hired.", path: '#contact' },
                    ].map((f, i) => (
                        <div
                            key={i}
                            className="pp-feat-card"
                            onClick={() => {
                                if (f.path.startsWith('#')) {
                                    document.getElementById(f.path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                    navigate(f.path);
                                }
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="pp-feat-icon">{f.icon}</div>
                            <h4>{f.title}</h4>
                            <p>{f.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ===== JOB BY CATEGORY SECTION ===== */}
            <section className="pp-cat-grid-section">
                <div className="pp-container">
                    <div className="pp-cat-grid-header">
                        <div className="pp-cat-header-left">
                            <h2 className="pp-cat-grid-title">Job by <span>Category</span></h2>
                            <p className="pp-cat-grid-subtitle">Browse career opportunities across growing industries. Quickly filter to find opportunities in development, design, marketing, finance, and more.</p>
                        </div>
                        <button className="pp-cat-explore-all" onClick={() => navigate('/browse/jobs')}>
                            Explore All <FaArrowRight size={12} />
                        </button>
                    </div>

                    <div className="pp-cat-grid-wrapper">
                        {categoryPage > 0 && (
                            <button
                                className="pp-cat-nav-btn left"
                                onClick={() => setCategoryPage(Math.max(0, categoryPage - 1))}
                            >
                                <FaChevronLeft />
                            </button>
                        )}

                        <div
                            className="pp-cat-grid"
                            onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
                            onTouchMove={(e) => setTouchEnd(e.targetTouches[0].clientX)}
                            onTouchEnd={() => {
                                if (!touchStart || !touchEnd) return;
                                const distance = touchStart - touchEnd;
                                const isLeftSwipe = distance > 50;
                                const isRightSwipe = distance < -50;
                                if (isLeftSwipe && categoryPage < 5) setCategoryPage(categoryPage + 1);
                                if (isRightSwipe && categoryPage > 0) setCategoryPage(categoryPage - 1);
                                setTouchStart(null);
                                setTouchEnd(null);
                            }}
                        >
                            {[
                                { name: 'IT & Software', icon: <FaCode /> },
                                { name: 'Healthcare & Medical', icon: <FaStethoscope /> },
                                { name: 'Finance & Accounting', icon: <FaSearchDollar /> },
                                { name: 'Engineering & Manufacturing', icon: <FaCogs /> },
                                { name: 'Education & Training', icon: <FaChalkboardTeacher /> },
                                { name: 'Sales & Marketing', icon: <FaBullhorn /> },
                                { name: 'Human Resources', icon: <FaUsers /> },
                                { name: 'Administration & Office Support', icon: <FaShieldAlt /> },
                                { name: 'Customer Service', icon: <FaHeadset /> },
                                { name: 'Hospitality & Tourism', icon: <FaConciergeBell /> },
                                { name: 'Logistics & Supply Chain', icon: <FaWarehouse /> },
                                { name: 'Design & Creative', icon: <FaPalette /> },
                                { name: 'Media & Communications', icon: <FaCamera /> },
                                { name: 'Legal', icon: <FaBalanceScale /> },
                                { name: 'Science & Research', icon: <FaMicroscope /> },
                                { name: 'Construction & Trades', icon: <FaHardHat /> },
                                { name: 'Retail & Consumer Goods', icon: <FaStore /> },
                                { name: 'Data Science & Analytics', icon: <FaChartBar /> },
                                { name: 'Banking & Insurance', icon: <FaBriefcase /> },
                                { name: 'Telecommunications', icon: <FaPlug /> },
                                { name: 'Government & Public Sector', icon: <FaBuilding /> },
                                { name: 'NGO & Non-Profit', icon: <FaGlobe /> },
                                { name: 'Real Estate', icon: <FaBuilding /> },
                                { name: 'Automotive', icon: <FaCar /> },
                                { name: 'Agriculture & Farming', icon: <FaLeaf /> },
                                { name: 'Energy & Utilities', icon: <FaBolt /> },
                                { name: 'Fashion & Apparel', icon: <FaTshirt /> },
                                { name: 'Food & Beverage', icon: <FaUtensils /> },
                                { name: 'Security & Law Enforcement', icon: <FaShieldAlt /> },
                                { name: 'Aviation & Aerospace', icon: <FaPlane /> },
                                { name: 'Marine & Shipping', icon: <FaShip /> },
                                { name: 'Sports & Fitness', icon: <FaDumbbell /> },
                                { name: 'Gaming & Animation', icon: <FaGamepad /> },
                                { name: 'Digital Marketing', icon: <FaChartLine /> },
                                { name: 'Content Writing', icon: <FaEdit /> },
                                { name: 'Travel & Leisure', icon: <FaMapMarkedAlt /> },
                                { name: 'E-commerce', icon: <FaShoppingCart /> },
                                { name: 'Cybersecurity', icon: <FaUserSecret /> },
                                { name: 'Cloud Computing', icon: <FaCloud /> },
                                { name: 'Quality Assurance', icon: <FaCheckCircle /> },
                                { name: 'Project Management', icon: <FaProjectDiagram /> },
                                { name: 'Art & Photography', icon: <FaCameraRetro /> },
                                { name: 'Music & Entertainment', icon: <FaMusic /> },
                                { name: 'Interior Design', icon: <FaPaintBrush /> },
                                { name: 'Social Services', icon: <FaHandHoldingHeart /> },
                                { name: 'Mining & Extraction', icon: <FaHardHat /> },
                                { name: 'Event Management', icon: <FaCalendarAlt /> },
                                { name: 'Others', icon: <FaTools /> }
                            ].slice(categoryPage * 8, (categoryPage * 8) + 8).map((cat, idx) => {
                                // Calculate a realistic looking mock count
                                const baseCount = allJobs.filter(j => j.category?.toLowerCase().includes(cat.name.split(' / ')[0].toLowerCase())).length;
                                const displayCount = baseCount > 0 ? baseCount : (idx % 15) + 2;

                                return (
                                    <div key={idx} className="pp-cat-card-new" onClick={() => navigate(`/browse/jobs?category=${encodeURIComponent(cat.name)}`)}>
                                        <div className="pp-cat-card-inner">
                                            <div className="pp-cat-icon-lg">{cat.icon}</div>
                                            <div className="pp-cat-info">
                                                <h3>{cat.name}</h3>
                                                <span className="pp-cat-positions">{displayCount} Open positions</span>
                                            </div>
                                            <div className="pp-cat-logos">
                                                <div className="pp-cat-logo-cluster">
                                                    <div className="pp-mini-logo"><FaBuilding /></div>
                                                    <div className="pp-mini-logo"><FaBriefcase /></div>
                                                    <div className="pp-mini-logo"><FaUsers /></div>
                                                    <span className="pp-mini-more">More &gt;</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="pp-cat-card-glow"></div>
                                    </div>
                                );
                            })}
                        </div>

                        {categoryPage < 5 && (
                            <button
                                className="pp-cat-nav-btn right"
                                onClick={() => setCategoryPage(Math.min(5, categoryPage + 1))}
                            >
                                <FaChevronRight />
                            </button>
                        )}
                    </div>

                    <div className="pp-cat-pagination">
                        {[0, 1, 2, 3, 4, 5].map(p => (
                            <span
                                key={p}
                                className={`pp-pg-dot ${categoryPage === p ? 'active' : ''}`}
                                onClick={() => setCategoryPage(p)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <main>
                {/* ===== JOBS ===== */}
                <section id="jobs" className="pp-section pp-bg-gray">
                    <div className="pp-inner">
                        <div className="pp-section-head">
                            <span className="pp-sec-label">Career Opportunities</span>
                            <h2>Featured Job Openings</h2>
                            <p>Latest openings from companies actively hiring right now.</p>
                        </div>
                        {jobs.length === 0 ? (
                            <div className="pp-empty-premium">
                                <div className="pp-ep-icon-wrap"><FaBriefcase /></div>
                                <h3>No Open Positions Found</h3>
                                <p>Our top-tier partner companies are preparing new roles. Sign up to get notified instantly.</p>
                                <div className="pp-ep-glow"></div>
                            </div>
                        ) : (
                            <div className="pp-grid pp-grid-4">
                                {jobs.map(job => (
                                    <div
                                        key={job._id}
                                        className="pp-job-card-premium"
                                        onClick={() => navigate(`/public-job/${job._id}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="pp-job-card-top">
                                            <div className="pp-job-icon-box">
                                                {job.companyId?.logoUrl ?
                                                    <img src={job.companyId.logoUrl.startsWith('http') ? job.companyId.logoUrl : `${API_URL}${job.companyId.logoUrl.startsWith('/') ? '' : '/'}${job.companyId.logoUrl}`} alt="Logo" /> :
                                                    <div className="pp-job-placeholder-icon">
                                                        <FaBriefcase />
                                                    </div>
                                                }
                                            </div>
                                            <div className="pp-job-badges">
                                                <span className={`pp-badge-type ${job.type?.toLowerCase()}`}>{job.type}</span>
                                                <span className="pp-badge-status">Active</span>
                                            </div>
                                        </div>

                                        <div className="pp-job-card-body">
                                            <h3 className="pp-job-title-premium">{job.title}</h3>
                                            <div className="pp-job-company-row">
                                                <div className="pp-co-info">
                                                    <span className="pp-co-name">{job.company}</span>
                                                    <span className="pp-co-verify"><FaCheckCircle /></span>
                                                </div>
                                            </div>

                                            <div className="pp-job-highlights">
                                                <div className="pp-jh-item">
                                                    <FaMapMarkerAlt />
                                                    <span>{job.location}</span>
                                                </div>
                                                <div className="pp-jh-item">
                                                    <TrendingUp size={14} />
                                                    <span>{job.salaryRange || 'Competitive'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pp-job-card-footer">
                                            <button
                                                className="pp-btn-apply-premium"
                                                onClick={() => handleApply(job._id)}
                                            >
                                                Apply Now <span><FaArrowRight /></span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="pp-sec-foot">
                            <button className="pp-btn-view-all" onClick={() => navigate('/browse/jobs')}>
                                View All Jobs <FaArrowRight />
                            </button>
                        </div>
                    </div>
                </section>

                {/* ===== CTA ===== */}
                <div className="pp-cta-banner">
                    <div className="pp-inner pp-cta-inner">
                        <div className="pp-cta-text">
                            <h2>Ready to Launch Your Career?</h2>
                            <p>Join thousands of professionals who have transformed their careers with ZenXStaffing.</p>
                        </div>
                        <button className="pp-btn pp-btn-cta" onClick={handleCreateAccount}>
                            {user ? 'Go to Dashboard' : 'Create Free Account'} <FaArrowRight />
                        </button>
                    </div>
                </div>

                {/* ===== COURSE BY CATEGORY SECTION ===== */}
                <section className="pp-cat-grid-section">
                    <div className="pp-container">
                        <div className="pp-cat-grid-header">
                            <div className="pp-cat-header-left">
                                <h2 className="pp-cat-grid-title">Courses by <span>Category</span></h2>
                                <p className="pp-cat-grid-subtitle">Level up your skills with globally recognized certifications. Discover trending programs in coding, data science, marketing, design, and professional development.</p>
                            </div>
                            <button className="pp-cat-explore-all" onClick={() => navigate('/browse/courses')}>
                                Explore All <FaArrowRight size={12} />
                            </button>
                        </div>

                        <div className="pp-cat-grid-wrapper">
                            {courseCategoryPage > 0 && (
                                <button
                                    className="pp-cat-nav-btn left"
                                    onClick={() => setCourseCategoryPage(Math.max(0, courseCategoryPage - 1))}
                                >
                                    <FaChevronLeft />
                                </button>
                            )}

                            <div
                                className="pp-cat-grid"
                                onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
                                onTouchMove={(e) => setTouchEnd(e.targetTouches[0].clientX)}
                                onTouchEnd={() => {
                                    if (!touchStart || !touchEnd) return;
                                    const distance = touchStart - touchEnd;
                                    const isLeftSwipe = distance > 50;
                                    const isRightSwipe = distance < -50;
                                    if (isLeftSwipe && courseCategoryPage < 5) setCourseCategoryPage(courseCategoryPage + 1);
                                    if (isRightSwipe && courseCategoryPage > 0) setCourseCategoryPage(courseCategoryPage - 1);
                                    setTouchStart(null);
                                    setTouchEnd(null);
                                }}
                            >
                                {courseCategories.slice(courseCategoryPage * 8, (courseCategoryPage * 8) + 8).map((cat, idx) => {
                                    const baseCount = allCourses.filter(c => c.category?.toLowerCase().includes(cat.name.toLowerCase())).length;
                                    const displayCount = baseCount > 0 ? baseCount : (idx % 12) + 3;

                                    return (
                                        <div key={idx} className="pp-cat-card-new" onClick={() => navigate(`/browse/courses?category=${encodeURIComponent(cat.name)}`)}>
                                            <div className="pp-cat-card-inner">
                                                <div className="pp-cat-icon-lg">{cat.icon}</div>
                                                <div className="pp-cat-info">
                                                    <h3>{cat.name}</h3>
                                                    <span className="pp-cat-positions">{displayCount} Featured Courses</span>
                                                </div>
                                                <div className="pp-cat-logos">
                                                    <div className="pp-cat-logo-cluster">
                                                        <div className="pp-mini-logo"><FaGraduationCap /></div>
                                                        <div className="pp-mini-logo"><FaLaptopCode /></div>
                                                        <div className="pp-mini-logo"><BookOpen size={14} /></div>
                                                        <span className="pp-mini-more">More &gt;</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="pp-cat-card-glow"></div>
                                        </div>
                                    );
                                })}
                            </div>

                            {courseCategoryPage < 5 && (
                                <button
                                    className="pp-cat-nav-btn right"
                                    onClick={() => setCourseCategoryPage(Math.min(5, courseCategoryPage + 1))}
                                >
                                    <FaChevronRight />
                                </button>
                            )}
                        </div>

                        <div className="pp-cat-pagination">
                            {[0, 1, 2, 3, 4, 5].map(p => (
                                <span
                                    key={p}
                                    className={`pp-pg-dot ${courseCategoryPage === p ? 'active' : ''}`}
                                    onClick={() => setCourseCategoryPage(p)}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* ===== COURSES ===== */}
                <section id="courses" className="pp-section pp-bg-white">
                    <div className="pp-inner">
                        <div className="pp-section-head">
                            <span className="pp-sec-label">What We Offer</span>
                            <h2>Featured Learning Programs</h2>
                            <p>Premium, industry-vetted curricula designed to transform careers.</p>
                        </div>
                        {courses.length === 0 ? (
                            <div className="pp-empty-premium">
                                <div className="pp-ep-icon-wrap"><FaGraduationCap /></div>
                                <h3>No Premium Courses Available</h3>
                                <p>We're currently curating our exclusive learning programs. Please check back shortly.</p>
                                <div className="pp-ep-glow"></div>
                            </div>
                        ) : (
                            <div className="pp-grid pp-grid-4">
                                {courses.map(course => (
                                    <div
                                        key={course._id}
                                        className="pp-course-card"
                                        onClick={() => handleCourseClick(course._id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="pp-c-thumb">
                                            {course.thumbnailUrl
                                                ? <img src={course.thumbnailUrl.startsWith('http') ? course.thumbnailUrl : `${API_URL}${course.thumbnailUrl.startsWith('/') ? '' : '/'}${course.thumbnailUrl}`} alt={course.title} />
                                                : <div className="pp-thumb-ph"><FaGraduationCap /></div>}
                                            <span className={`pp-level pp-level-${(course.difficultyLevel || 'Beginner').toLowerCase()}`}>{course.difficultyLevel}</span>
                                        </div>
                                        <div className="pp-c-body">
                                            <span className="pp-c-cat">{course.category}</span>
                                            <h3>{course.title}</h3>
                                            <p className="pp-c-tutor">By {course.tutorId?.name || 'Expert Instructor'}</p>
                                            <div className="pp-c-foot">
                                                <span className="pp-price">{course.pricingType === 'Free' ? <span className="pp-free">Free</span> : `$${course.price}`}</span>
                                                <button
                                                    className="pp-btn pp-btn-enroll"
                                                    onClick={(e) => { e.stopPropagation(); handleCourseClick(course._id); }}
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="pp-sec-foot">
                            <button className="pp-btn-view-all" onClick={() => navigate('/browse/courses')}>
                                View All Courses <FaArrowRight />
                            </button>
                        </div>
                    </div>
                </section>

                {/* ===== ABOUT ===== */}
                <section id="about" className="pp-section pp-about-section">
                    <div className="pp-inner">
                        <div className="pp-about-container">
                            <div className="pp-about-content">
                                <div className="pp-about-badge">
                                    <FaRocket className="pp-about-badge-icon" />
                                    <span>Our Mission</span>
                                </div>
                                <h2 className="pp-about-title">
                                    Campus to Career.<br />
                                    <span className="pp-highlight">Groomed &amp; Placed.</span>
                                </h2>
                                <p className="pp-about-desc">
                                    ZenXStaffing is a full-cycle career acceleration platform — seamlessly linking premium industry education with direct hiring pipelines to future-proof your career.
                                </p>

                                <div className="pp-about-features">
                                    <div className="pp-af-item">
                                        <div className="pp-af-icon"><FaCheckCircle /></div>
                                        <div className="pp-af-text">
                                            <h4>Industry-Mapped Curriculum</h4>
                                            <p>Learn exactly what top-tier companies are looking for.</p>
                                        </div>
                                    </div>
                                    <div className="pp-af-item">
                                        <div className="pp-af-icon"><FaUsers /></div>
                                        <div className="pp-af-text">
                                            <h4>1-on-1 Expert Mentorship</h4>
                                            <p>Gain insights directly from seasoned professionals.</p>
                                        </div>
                                    </div>
                                    <div className="pp-af-item">
                                        <div className="pp-af-icon"><FaBuilding /></div>
                                        <div className="pp-af-text">
                                            <h4>Direct Recruiter Access</h4>
                                            <p>Skip the line with exclusive hiring partnerships.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pp-about-actions">
                                    <button className="pp-btn pp-btn-solid pp-btn-large" onClick={() => navigate('/auth')}>
                                        Join the Platform <FaArrowRight />
                                    </button>
                                </div>
                            </div>

                            <div className="pp-about-visual">
                                <div className="pp-av-backdrop"></div>
                                <div className="pp-av-main-card">
                                    <img src="/logo1.png" alt="ZenXStaffing" className="pp-about-logo" />
                                    <div className="pp-av-floating-badge top-right">
                                        <div className="pp-av-fb-icon"><FaGraduationCap /></div>
                                        <span>Elite Training</span>
                                    </div>
                                    <div className="pp-av-floating-badge bottom-left">
                                        <div className="pp-av-fb-icon"><FaBriefcase /></div>
                                        <span>Guaranteed Interviews*</span>
                                    </div>
                                    <div className="pp-av-glow"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                {/* ===== RESOURCES ===== */}
                <section id="resources" className="pp-section pp-resources-section">
                    <div className="pp-inner">
                        <div className="pp-section-head">
                            <span className="pp-sec-label pp-sec-label-light">Learning Materials</span>
                            <h2 className="pp-text-light">Career Resources</h2>
                            <p className="pp-text-light-muted">Free guides, templates, and materials to help you succeed.</p>
                        </div>
                        <div className="pp-grid pp-grid-3">
                            {[
                                { title: 'Resume Writing Masterclass', icon: <BookOpen />, desc: 'Learn how to craft a resume that beats ATS systems and catches recruiter eyes.' },
                                { title: 'Interview Cheat Sheet', icon: <TrendingUp />, desc: 'Top 50 technical and HR questions asked by Fortune 500 companies.' },
                                { title: 'Salary Negotiation Guide', icon: <Shield />, desc: 'Actionable scripts to negotiate a 20%+ increase on your next offer.' }
                            ].map((res, i) => (
                                <div key={i} className="pp-feat-card pp-res-card">
                                    <div className="pp-feat-icon">{res.icon}</div>
                                    <h4>{res.title}</h4>
                                    <p>{res.desc}</p>
                                    <button className="pp-btn-text">Download Free</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ===== CONTACT ===== */}
                <section id="contact" className="pp-section pp-bg-white pp-contact-section">
                    <div className="pp-inner">
                        <div className="pp-contact-grid">
                            <div className="pp-contact-info">
                                <span className="pp-sec-label">Get In Touch</span>
                                <h2>Have Questions? <br />We're Here to Help.</h2>
                                <p>Whether you need help selecting a course or understanding our placement process, our team is ready to assist you.</p>
                                <div className="pp-contact-methods">
                                    <div className="pp-method">
                                        <div className="pp-method-icon"><Mail /></div>
                                        <div>
                                            <h4>Email Us</h4>
                                            <p>support@zenxstaffing.com</p>
                                        </div>
                                    </div>
                                    <div className="pp-method">
                                        <div className="pp-method-icon"><Globe /></div>
                                        <div>
                                            <h4>Global Support</h4>
                                            <p>Available 24/7 for our students</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="pp-contact-form">
                                <form onSubmit={(e) => { e.preventDefault(); Swal.fire('Sent!', 'We will get back to you shortly.', 'success') }}>
                                    <div className="pp-input-group">
                                        <input type="text" placeholder="Your Name" required />
                                    </div>
                                    <div className="pp-input-group">
                                        <input type="email" placeholder="Your Email" required />
                                    </div>
                                    <div className="pp-input-group">
                                        <textarea placeholder="How can we help you?" rows="4" required></textarea>
                                    </div>
                                    <button type="submit" className="pp-btn pp-btn-solid pp-w-full">Send Message</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* ===== FOOTER ===== */}
            <footer className="pp-footer">
                <div className="pp-footer-inner">
                    <div className="pp-footer-brand">
                        <img src="/logo1.png" alt="ZenXStaffing" className="pp-footer-logo" />
                        <p>Campus to Career | Groomed &amp; Placed</p>
                    </div>
                    <div className="pp-footer-links">
                        <a href="#courses" onClick={(e) => scrollToSection(e, 'courses')}>Courses</a>
                        <a href="#jobs" onClick={(e) => scrollToSection(e, 'jobs')}>Jobs</a>
                        <a href="#about" onClick={(e) => scrollToSection(e, 'about')}>About</a>
                        <a href="#resources" onClick={(e) => scrollToSection(e, 'resources')}>Resources</a>
                        <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>Contact</a>
                        {user
                            ? <button className="pp-footer-link-btn" onClick={handleDashboard}>Dashboard</button>
                            : <button className="pp-footer-link-btn" onClick={() => navigate('/auth')}>Sign In</button>
                        }
                    </div>
                </div>
                <div className="pp-footer-bottom">
                    <p>&copy; {new Date().getFullYear()} ZenXStaffing. All rights reserved.</p>
                </div>
            </footer>
            {/* ===== SCROLL TO TOP ===== */}
            <button
                className={`pp-scroll-top ${showScrollTop ? 'visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Scroll to top"
            >
                <FaArrowUp />
            </button>

            {/* ===== MOBILE BOTTOM TAB BAR (Carbon Style) ===== */}
            <div className="pp-mobile-tab-bar">
                <a href="#courses" onClick={(e) => scrollToSection(e, 'courses')} className="pp-tb-item">
                    <FaGraduationCap />
                    <span>Courses</span>
                </a>
                <a href="#jobs" onClick={(e) => scrollToSection(e, 'jobs')} className="pp-tb-item">
                    <FaBriefcase />
                    <span>Jobs</span>
                </a>
                <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className="pp-tb-item pp-tb-main">
                    <div className="pp-tb-center-btn">
                        <img src="/logo.png" alt="Home" style={{ width: '44px', height: '44px', objectFit: 'contain' }} />
                    </div>
                    <span>Home</span>
                </a>
                <a href="#resources" onClick={(e) => scrollToSection(e, 'resources')} className="pp-tb-item">
                    <BookOpen size={20} />
                    <span>Guides</span>
                </a>
                <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="pp-tb-item">
                    <FaMapMarkerAlt />
                    <span>Contact</span>
                </a>
            </div>
        </div>
    );
};

export default PublicPortal;
